"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";

import LanguageSelector from "@/components/LanguageSelector";
import DirectGoogleLoginButton from "@/components/auth/DirectGoogleLoginButton";
import ProfileMenu from "@/components/ProfileMenu/ProfileMenuButton";
import { performLogout } from "@/lib/logout";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

export default function ResponsiveHeader({
  navLinks,
  brandHref = "/",
  brandLabel = "JOBolar.Ai",
  loginHref = "/login",
  signupHref = "/signup",
  accountHref = "/onboarding?tab=profile#profile",
  loginLabel,
  signupLabel,
  accountLabelFallback,
  showLanguageSelector = true,
}) {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "nav"), [locale]);

  const resolvedNavLinks = navLinks || [
    { label: t.how, href: "/#how", scroll: true },
    { label: t.features, href: "/#features", scroll: true },
    { label: t.reviews, href: "/#reviews", scroll: true },
    { label: t.pricings, href: "/#pricing", scroll: true }
  ];
  const resolvedLoginLabel = loginLabel || t.login;
  const resolvedSignupLabel = signupLabel || t.signup;
  const resolvedAccountFallback = accountLabelFallback || t.view_account;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const { data: session, status } = useSession();
  const isAuthenticated = status === "authenticated";
  const accountLabel =
    session?.user?.name || session?.user?.email || resolvedAccountFallback;

  const handleToggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    setIsAccountMenuOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsAccountMenuOpen(false);
  };

  const handleToggleAccountMenu = () => {
    setIsAccountMenuOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    setIsAccountMenuOpen(false);
    await performLogout(loginHref);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          href={brandHref}
          className="flex items-center"
          aria-label={`${brandLabel} — ${t.home}`}
          onClick={closeMenu}
        >
          <Image
            src="/image/JobolarAiLogo.png"
            alt="JOBolar.Ai"
            width={160}
            height={48}
            priority={brandHref === "/"}
            className="h-10 w-auto md:h-12"
          />
          <span className="sr-only">{brandLabel}</span>
        </Link>

        <nav className="hidden items-center gap-4 text-sm lg:flex xl:gap-6" aria-label={t.primary_navigation}>
          {resolvedNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              scroll={link.scroll}
              className="font-bold text-slate-700 transition-colors hover:text-indigo-600"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {showLanguageSelector ? <LanguageSelector /> : null}
          {isAuthenticated ? (
            <ProfileMenu user={session?.user} onLogout={handleLogout} />
          ) : (
            <>
              <DirectGoogleLoginButton
                className="rounded-xl border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-wait disabled:opacity-70"
              >
                {resolvedLoginLabel}
              </DirectGoogleLoginButton>
              <Link
                href={signupHref}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                {resolvedSignupLabel}
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 lg:hidden"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-nav"
          onClick={handleToggleMenu}
        >
          <span className="sr-only">{t.toggle_nav}</span>
          <svg
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {isMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>

      <div
        id="mobile-nav"
        className={`${
          isMenuOpen ? "max-h-screen border-t border-slate-200" : "max-h-0"
        } overflow-hidden bg-white transition-[max-height] duration-300 lg:hidden`}
      >
        <nav className="flex flex-col gap-1 px-4 pb-4 pt-4 text-sm" aria-label={t.mobile_navigation}>
          {resolvedNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              scroll={link.scroll}
              className="rounded-lg px-4 py-3 font-bold text-slate-700 hover:bg-slate-100 active:bg-slate-200"
              onClick={closeMenu}
            >
              {link.label}
            </Link>
          ))}
          {showLanguageSelector ? (
            <div className="rounded-lg px-3 py-2">
              <LanguageSelector />
            </div>
          ) : null}
          {isAuthenticated ? (
            <div className="flex flex-col gap-2 rounded-lg px-3 py-2">
              <button
                type="button"
                onClick={handleToggleAccountMenu}
                className="rounded-lg border border-slate-300 px-4 py-3 text-left font-medium text-slate-700"
              >
                {accountLabel}
              </button>
              {isAccountMenuOpen ? (
                <>
                  <Link
                    href={accountHref}
                    className="rounded-lg px-4 py-3 text-slate-700 hover:bg-slate-100 active:bg-slate-200"
                    onClick={closeMenu}
                  >
                    {t.view_account}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                    className="rounded-lg px-4 py-3 text-left text-red-600 hover:bg-red-50 active:bg-red-100"
                  >
                    {t.logout}
                  </button>
                </>
              ) : null}
            </div>
          ) : (
            <>
              <DirectGoogleLoginButton
                onStart={closeMenu}
                containerClassName="w-full"
                className="w-full rounded-xl px-4 py-3 text-left font-semibold transition hover:bg-slate-100 active:bg-slate-200 disabled:cursor-wait disabled:opacity-70"
              >
                {resolvedLoginLabel}
              </DirectGoogleLoginButton>
              <Link
                href={signupHref}
                className="rounded-lg bg-indigo-600 px-4 py-3 font-medium text-white hover:bg-indigo-700 active:bg-indigo-800"
                onClick={closeMenu}
              >
                {resolvedSignupLabel}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
